/**
 * `CalculatePhysicsVector` — where movement is actually produced.
 *
 * A player is never a physics capsule that happens to play a matching animation. When a
 * clip is chosen, its root motion is thrown away and RE-INTEGRATED here, one step per
 * frame, through a chain of limits: how fast this player can accelerate, how sharply he can
 * corner at this speed, how far the clip may be bent toward where the controller actually
 * wanted to go. What comes out is a fresh per-frame position track. That track, not the
 * clip's own, is what moves him.
 *
 * This is the single most important function in the game. Source:
 * humanoidbase.cpp:2014-2544, transcribed against the C++ rather than the summary — the
 * summary has one inverted comment and one wrong branch structure, both noted below.
 *
 * Everything disabled in the shipped build is omitted rather than written and skipped:
 * cornering braking, maximum accel/decel, brake-on-touch and the body-direction cheat are
 * all `false` at humanoidbase.cpp:2138-2146.
 */

import { Vector3 } from 'three';
import {
  angle2D,
  clamp,
  curve,
  normalizedClamp,
  normalizedMax,
  normalizedOrZero,
  normalizedTo,
  rotate2D,
  signSide,
  vecLength,
} from '../../core/math';
import { variable, type AnimClip } from './clip';
import {
  ANIM_SPRINT_VELOCITY,
  DRIBBLE_VELOCITY,
  IDLE_DRIBBLE_SWITCH,
  SPRINT_VELOCITY,
  Velocity,
  WALK_SPRINT_SWITCH,
  floatToEnumVelocity,
} from './velocity';
import type { SpatialState } from './spatial';

const TIME_STEP_MS = 10;

/**
 * What the solver needs to know about the player.
 *
 * `stat` is the VIRTUAL lookup (player.cpp:526-540), which scales the raw database value
 * by an AI handicap and by fatigue. `maxVelocity` deliberately is not: it comes from the
 * RAW stat (playerbase.cpp:131-139), so a tired player still has his top speed and simply
 * cannot reach it as easily.
 */
export interface PlayerPhysics {
  /**
   * playerbase.cpp:131-139 — `sprintVelocity * (0.9 + rawStat(physical_velocity) * 0.1)`.
   *
   * Fixed for the whole match: it is read through `PlayerBase::GetStat`, which unlike
   * `Player::GetStat` applies no modifier at all, so tiredness never lowers a player's top
   * speed. What tiredness takes away is how often he is willing to reach it.
   */
  maxVelocity: number;
  /**
   * How fresh he is: 1 at kick-off, down to a floor of 0.01. Written by the match once a
   * tick; `stat` below is expected to scale itself by it, which is how the original applies
   * tiredness to every skill at once (`player.cpp:526-540`). Absent means fresh.
   */
  fatigueFactorInv?: number;
  /**
   * How much an injury is currently taking off every skill at once: 1 uninjured, down toward
   * a floor for a serious knock. Written by the match the same way `fatigueFactorInv` is —
   * see `MatchPlayer.setInjury`, called from `Match.applyInjury` — and read through the SAME
   * multiplication in `main.ts`'s `stat` closure rather than a parallel one, per the plan
   * (`docs/design/violence-and-variety.md`, tranche 2.4): tiredness already scales every
   * skill through one modifier, and this is a second term in that same place. Absent means
   * uninjured, exactly as an absent `fatigueFactorInv` means fresh.
   */
  injuryFactorInv?: number;
  stat(name: string): number;
  /**
   * playerbase.cpp:141-146 — 1 just after a touch, decaying to 0 over `decayMs`.
   *
   * Load-bearing, and quietly so: it is the only input to the post-touch power penalty in
   * `powerFactor` below, and a stub that always answers 0 switches that penalty off entirely
   * without anything failing. A player who has just knocked the ball forward would then brake
   * and turn as sharply as one who has not touched it in a minute. The real value is the match
   * player's own, measured against the match clock, and it is wired up in `main.ts` once the
   * match exists. Test doubles are welcome to return 0; production is not.
   */
  lastTouchBias(decayMs: number): number;
  /**
   * How far the body has been pushed off its animated position by collisions.
   * Decays 5% a tick, capped at 0.1 m (humanoidbase.cpp:574-575, 1041).
   */
  decayingPositionOffset: Vector3;
}

/**
 * The two engine-configurable feel knobs, both sliders in the settings menu.
 *
 * The shipped config file is empty, so both sit at 0.5, at which three separate formulas
 * in here collapse to exactly 1.0. Implemented in full anyway, because the sliders are
 * real; tuned against the collapsed values, because that is what the original ships as.
 */
export interface AnimConfig {
  /** `gameplay_agilityfactor`, default 0.5 (gamedefines.hpp:43). */
  agilityFactor: number;
  /** `gameplay_accelerationfactor`, default 0.5 (gamedefines.hpp:44). */
  accelerationFactor: number;
}

export const DEFAULT_ANIM_CONFIG: AnimConfig = { agilityFactor: 0.5, accelerationFactor: 0.5 };

export interface PhysicsResult {
  /** World-space m/s at the end of the clip. */
  resultingMovement: Vector3;
  /**
   * One entry per frame, relative to the start position, already in world orientation.
   * Entry 0 is where the player is after the FIRST 10 ms, not where he started.
   */
  positions: Vector3[];
  /** Radians. Becomes the second half of the rotation smuggle. */
  rotationOffset: number;
}

/**
 * `CalculateMovementAtFrame` (humanoid_utils.cpp:68-100) — the velocity at a frame of a
 * position track, in m/s.
 *
 * Two special cases at the ends, and one quiet asymmetry: the averaging window skips
 * frame 0 (`frame > 0`, not `>= 0`), so asking for frame 1 with a window of 1 averages two
 * samples, never three.
 */
export function movementAtFrame(
  positions: Vector3[],
  frameNum: number,
  smoothFrames = 0,
  out = new Vector3(),
): Vector3 {
  const n = positions.length;
  if (n < 2) return out.set(0, 0, 0);

  if (frameNum === n - 1) {
    return out.copy(positions[n - 1]!).sub(positions[n - 2]!).multiplyScalar(100);
  }
  if (frameNum === 0) {
    return out.copy(positions[1]!).sub(positions[0]!).multiplyScalar(100);
  }

  out.set(0, 0, 0);
  let count = 0;
  for (let frame = frameNum - smoothFrames; frame <= frameNum + smoothFrames; frame++) {
    if (frame > 0 && frame < n) {
      out.x += (positions[frame]!.x - positions[frame - 1]!.x) * 100;
      out.y += (positions[frame]!.y - positions[frame - 1]!.y) * 100;
      out.z += (positions[frame]!.z - positions[frame - 1]!.z) * 100;
      count++;
    }
  }
  if (count > 0) out.divideScalar(count);
  return out;
}

/**
 * `StretchSprintTo` (humanoid_utils.cpp:129-144) — map the speed the sprint clips were
 * drawn at onto the speed THIS player runs at.
 *
 * Everything below walking pace passes through untouched; only the sprint overhang is
 * rescaled. This is how one set of sprint animations serves a slow centre-half and a fast
 * winger without either looking wrong.
 */
export function stretchSprintTo(
  inputVelocity: number,
  inputSpaceMaxVelocity: number,
  targetMaxVelocity: number,
): number {
  if (inputVelocity < WALK_SPRINT_SWITCH) return inputVelocity;
  const sprintage = inputVelocity - WALK_SPRINT_SWITCH;
  const oldLength = inputSpaceMaxVelocity - WALK_SPRINT_SWITCH;
  const newLength = targetMaxVelocity - WALK_SPRINT_SWITCH;
  return WALK_SPRINT_SWITCH + sprintage * (newLength / oldLength);
}

/**
 * `CalculateBiasForFastCornering` (humanoid_utils.cpp:53-66).
 *
 * Not used by the solver — it feeds the clip RANKER — but it lives with its sibling here.
 * Answers "how much should I want to slow down before making this turn?": zero going
 * straight, near one turning back on yourself at speed.
 */
export function calculateBiasForFastCornering(
  currentMovement: Vector3,
  desiredMovement: Vector3,
  veloPow = 1.0,
  bias = 1.0,
): number {
  // The fallback for a zero desired movement is the CURRENT movement, un-normalised, which
  // makes the angle below exactly zero and the whole result zero.
  const desired = new Vector3();
  if (
    Math.abs(desiredMovement.x) < 1e-6 &&
    Math.abs(desiredMovement.y) < 1e-6 &&
    Math.abs(desiredMovement.z) < 1e-6
  ) {
    desired.copy(currentMovement);
  } else {
    normalizedOrZero(desiredMovement, desired);
  }
  const angle = angle2D(desired, currentMovement);
  const currentMovementBias = Math.sin(Math.abs(angle) - 0.5 * Math.PI) * 0.5 + 0.5;
  // Note the divisor is sprintVelocity - 0.5, not sprintVelocity.
  const velocityBias = Math.pow(
    clamp(vecLength(currentMovement) / (SPRINT_VELOCITY - 0.5), 0, 1),
    veloPow,
  );
  return velocityBias * currentMovementBias * bias;
}

// ---------------------------------------------------------------------------

/**
 * `physicsBias` — how far the solver is allowed to depart from the clip at all.
 *
 * 1.0 means "the clip is a suggestion, steer freely"; 0.0 means "play it exactly as drawn".
 * A shot or a pass is 0.0 because bending it would break the animation's contact with the
 * ball. humanoidbase.cpp:2119-2136 — sequential multiplications, not a lookup, so the
 * special-state test at the end applies on top of whatever the type said.
 */
function physicsBiasFor(clip: AnimClip): number {
  let bias = 1.0;
  switch (clip.animType) {
    case 'movement':
    case 'ballcontrol':
    case 'trap':
    case 'sliding':
      bias *= 1.0;
      break;
    case 'shortpass':
    case 'highpass':
    case 'shot':
    case 'deflect':
    case 'special':
      bias *= 0.0;
      break;
    case 'interfere':
      bias *= 0.5;
      break;
    case 'trip':
      bias *= variable(clip, 'triptype') === '1' ? 0.5 : 0.0;
      break;
    default:
      break;
  }
  if (variable(clip, 'incoming_special_state') !== '') bias *= 0.0;
  return bias;
}

/**
 * `outgoingSwitchBias` — how much of the clip's own outgoing movement overrides what the
 * controller asked for. humanoidbase.cpp:2031-2050.
 *
 * THE CHAIN ORDER IS THE POINT, and the specification document gets this wrong. Every
 * ball-touch type has a touch frame, so if the explicit zero branches were not FIRST, a
 * dribble touch would fall through to the `touch` case, take 1.0, and ignore the player's
 * stick completely. Only passes and shots actually reach that fallback.
 */
function outgoingSwitchBiasFor(clip: AnimClip, touch: boolean): number {
  let bias: number;
  if (clip.animType === 'ballcontrol') bias = 0.0;
  else if (clip.animType === 'trap') bias = 0.0;
  else if (clip.animType === 'interfere') bias = 0.0;
  else if (clip.animType === 'deflect') bias = 1.0;
  else if (clip.animType === 'sliding') bias = 0.0;
  else if (clip.animType === 'special') bias = 1.0;
  else if (clip.animType === 'trip') bias = 0.5;
  else if (touch) bias = 1.0;
  else bias = 0.0;

  if (
    variable(clip, 'incoming_special_state') !== '' ||
    variable(clip, 'outgoing_special_state') !== ''
  ) {
    bias = 1.0;
  }
  return bias;
}

/**
 * Turn a clip plus a wish into an actual path across the pitch.
 *
 * @param desiredMovement world-space m/s the controller asked for; ignored unless
 *                        `useDesiredMovement`
 * @param desiredBodyDirectionRel where he wants to be facing, relative to travel — only
 *                        consulted when the desired movement is too slow to imply a heading
 */
export function calculatePhysicsVector(
  clip: AnimClip,
  useDesiredMovement: boolean,
  desiredMovement: Vector3,
  desiredBodyDirectionRel: Vector3,
  spatial: SpatialState,
  player: PlayerPhysics,
  config: AnimConfig = DEFAULT_ANIM_CONFIG,
): PhysicsResult {
  const positions: Vector3[] = [];

  // ---- setup -------------------------------------------------------------
  const animTouchFrame = clip.touchFrame;
  const touch = animTouchFrame > 0;

  const statAgility = player.stat('physical_agility');
  const statAcceleration = player.stat('physical_acceleration');
  const statDribble = player.stat('technical_dribble');

  const outgoingSwitchBias = outgoingSwitchBiasFor(clip, touch);
  // incomingSwitchBias is hard-coded 0 (humanoidbase.cpp:2026) with the comment that
  // anything else "may result in unpuristic behavior", so the current movement passes
  // through untouched.
  const adaptedCurrentMovement = spatial.movement.clone();

  const predictedOutgoingMovement = rotate2D(clip.outgoingMovement, spatial.angle);
  const velocified = useDesiredMovement ? desiredMovement : predictedOutgoingMovement;
  const adaptedDesiredMovement = predictedOutgoingMovement
    .clone()
    .multiplyScalar(outgoingSwitchBias)
    .addScaledVector(velocified, 1 - outgoingSwitchBias);

  // Only the sprint stretch uses this scaled value; air resistance deliberately re-reads
  // the unscaled one (humanoidbase.cpp:2424). Do not merge the two.
  let maxVelocity = player.maxVelocity;
  if (touch) maxVelocity *= 0.92;

  const difficultyFactor = clip.difficulty;
  const difficultyPenaltyFactor = Math.pow(
    clamp(difficultyFactor * (1 - (statAgility * 0.2 + statAcceleration * 0.2)) * 2, 0, 1),
    0.7,
  );

  let powerFactor =
    1 - clamp(Math.pow(player.lastTouchBias(1000), 0.8) * (0.8 - statDribble * 0.3), 0, 0.4);
  powerFactor *=
    1 -
    clamp(
      vecLength(player.decayingPositionOffset) * (10 - player.stat('physical_balance') * 5) - 0.1,
      0,
      0.3,
    );

  const physicsBias = physicsBiasFor(clip);

  // ---- how far the clip may be rotated -----------------------------------
  let maxUnderAnimAngle = 0.125 * Math.PI;
  let maxOverAnimAngle = 0.125 * Math.PI;
  let maxStraightAnimAngle = 0.125 * Math.PI;

  if (touch) {
    let bonus =
      1 -
      Math.pow(
        normalizedClamp(
          vecLength(adaptedCurrentMovement.clone().add(predictedOutgoingMovement)) * 0.5,
          0,
          SPRINT_VELOCITY,
        ),
        0.8,
      ) *
        0.8;
    bonus *= 0.6 + 0.4 * player.stat('technical_ballcontrol');
    maxUnderAnimAngle = 0.2 * Math.PI * bonus;
    maxOverAnimAngle = 0;
    maxStraightAnimAngle = 0.1 * Math.PI * bonus;
  }
  if (clip.animType === 'sliding') {
    // Deliberately after the touch block: slides have a touch frame, and this restores
    // their freedom to swing either way.
    maxUnderAnimAngle = 0.5 * Math.PI;
    maxOverAnimAngle = 0.5 * Math.PI;
    maxStraightAnimAngle = 0.5 * Math.PI;
  }

  const accelerationMultiplier = 0.5 + config.accelerationFactor;

  // ---- the single rotation decision --------------------------------------
  //
  // Decided once here, then ramped in linearly across the clip. Beyond a quarter turn the
  // solver gives up entirely and leaves the clip alone, on the grounds that the ranker
  // should have offered a better clip.
  //
  // WHICH LIMIT APPLIES WHERE is easy to get backwards, and the specification document's
  // comment does get it backwards. Verified against humanoidbase.cpp:2165-2168: wanting to
  // turn the SAME way the clip already turns is "over"-rotating it, and for a touch clip
  // that allowance is exactly zero. Wanting to turn the other way is "under"-rotating, and
  // that is the one with room in it. So a touch clip can be straightened out but never
  // curled further into its own turn.
  let toDesiredAngleCapped = 0;
  if (physicsBias > 0) {
    const animOutgoing = normalizedOrZero(predictedOutgoingMovement);
    if (floatToEnumVelocity(vecLength(predictedOutgoingMovement)) === Velocity.Idle) {
      rotate2D(clip.outgoingDirection, spatial.angle, animOutgoing);
    }
    const desiredVector = normalizedOrZero(adaptedDesiredMovement);
    if (floatToEnumVelocity(vecLength(adaptedDesiredMovement)) === Velocity.Idle) {
      rotate2D(desiredBodyDirectionRel, spatial.angle, desiredVector);
    }

    const toDesiredAngle = angle2D(desiredVector, animOutgoing);
    if (Math.abs(toDesiredAngle) <= 0.5 * Math.PI || clip.animType === 'sliding') {
      const animChange = angle2D(animOutgoing, spatial.directionVec);
      if (Math.abs(animChange) > 0.06 * Math.PI) {
        toDesiredAngleCapped =
          signSide(toDesiredAngle) === signSide(animChange)
            ? clamp(toDesiredAngle, -maxOverAnimAngle, maxOverAnimAngle)
            : clamp(toDesiredAngle, -maxUnderAnimAngle, maxUnderAnimAngle);
      } else {
        toDesiredAngleCapped = clamp(toDesiredAngle, -maxStraightAnimAngle, maxStraightAnimAngle);
      }
    }
  }

  // ---- the integration loop ----------------------------------------------
  const temporalMovement = adaptedCurrentMovement.clone();
  const currentPosition = new Vector3(0, 0, 0);

  const animMovement = new Vector3();
  const adaptedAnimMovement = new Vector3();
  const toDesired = new Vector3();
  const scratch = new Vector3();

  for (let timeMs = 0; timeMs < clip.frameCount * 10; timeMs += TIME_STEP_MS) {
    // (a) where in the clip are we, in time warped by how agile this player is?
    //
    // A sluggish player's exponent is above 1, which DELAYS the clip's velocity profile —
    // he gets going late. An agile one anticipates it. This is most of why two players
    // running the same animation do not feel the same.
    const frameBias = (timeMs + 10) / ((clip.effectiveFrameCount + 1) * 10);

    let lagExp = 1.0;
    if (physicsBias > 0 && (clip.animType === 'ballcontrol' || clip.animType === 'movement')) {
      lagExp = 1.4 - config.agilityFactor * 0.8;
      lagExp *= 1.2 - statAgility * 0.4;
      lagExp += touch
        ? -0.1 + clamp(difficultyFactor * 0.4, 0, 0.5)
        : -0.2 + clamp(difficultyFactor * 0.2, 0, 0.2);
      lagExp = clamp(lagExp, 0.25, 4.0);
      // Do not let a player over-rotate before he has actually struck the ball.
      if (touch && timeMs < animTouchFrame * 10) lagExp = Math.max(lagExp, 0.7);
      lagExp = lagExp * physicsBias + 1.0 * (1 - physicsBias);
    }
    const adaptedFrameBias = Math.pow(frameBias, lagExp);

    // The C++ passes this through an `unsigned int` parameter, so it truncates.
    movementAtFrame(
      clip.positionCache,
      Math.floor(clip.effectiveFrameCount * adaptedFrameBias),
      1,
      animMovement,
    );
    rotate2D(animMovement, spatial.angle, animMovement);

    const animVelo = vecLength(animMovement);
    adaptedAnimMovement.copy(animMovement);
    let adaptedAnimVelo = animVelo;

    // (b) stretch the authored sprint speed to this player's real top speed
    if (
      animVelo > WALK_SPRINT_SWITCH &&
      (clip.animType === 'movement' || clip.animType === 'ballcontrol' || clip.animType === 'trap')
    ) {
      if (maxVelocity > animVelo) {
        adaptedAnimVelo = stretchSprintTo(animVelo, ANIM_SPRINT_VELOCITY, maxVelocity);
        normalizedOrZero(adaptedAnimMovement, adaptedAnimMovement).multiplyScalar(adaptedAnimVelo);
      }
    }

    // (c) let the wanted speed pull the clip's speed, within a window that narrows as the
    //     clip runs out
    let maxSlower = touch ? 1.2 : 1.6;
    let maxFaster = 0;
    if (vecLength(temporalMovement) > adaptedAnimVelo) {
      maxFaster = Math.min(
        1.0 * (1 - frameBias),
        Math.max(0, vecLength(temporalMovement) - adaptedAnimVelo),
      );
    }
    if (maxFaster > 0) {
      // normalizedMax is a CAP: a short vector comes back unchanged, not as a unit vector.
      maxFaster *= Math.max(
        0,
        normalizedMax(adaptedAnimMovement, 1.0, scratch).dot(
          normalizedOrZero(adaptedDesiredMovement),
        ),
      );
    }
    if (clip.animType === 'sliding') maxFaster = 100;

    adaptedAnimVelo = clamp(
      vecLength(adaptedDesiredMovement),
      adaptedAnimVelo - maxSlower,
      adaptedAnimVelo + maxFaster,
    );
    // A clip frame with no root motion at all stays still no matter what speed is asked
    // for: normalising a zero vector gives zero.
    normalizedOrZero(adaptedAnimMovement, adaptedAnimMovement).multiplyScalar(adaptedAnimVelo);

    // (d) ramp the one rotation decision in across the clip, on the RAW frame bias
    const resulting = rotate2D(adaptedAnimMovement, toDesiredAngleCapped * frameBias);

    // (e) for clips the solver may not fully steer, blend back toward the untouched clip.
    //     The partner is the clip motion BEFORE stretching, window and rotation.
    resulting.multiplyScalar(physicsBias).addScaledVector(animMovement, 1 - physicsBias);

    // (f) the change we would like this frame
    toDesired.copy(resulting).sub(temporalMovement);

    // (g) cornering limit — the faster you are going, the wider you must turn. Note it
    //     sheds SPEED rather than refusing the angle, which is what makes a hard turn
    //     look like a player planting his foot and slowing to make it.
    {
      scratch.copy(temporalMovement).add(toDesired);
      if (vecLength(temporalMovement) > IDLE_DRIBBLE_SWITCH && vecLength(scratch) > IDLE_DRIBBLE_SWITCH) {
        const angle = angle2D(normalizedOrZero(scratch), normalizedOrZero(temporalMovement));
        let maxAngleFactor = 1.0 * (TIME_STEP_MS / 1000);
        maxAngleFactor *= 0.7 + 0.3 * statAgility;
        if (!touch) maxAngleFactor *= 1.5;
        let maxAngle = maxAngleFactor * Math.PI;
        maxAngle /= normalizedClamp(vecLength(temporalMovement), 0, SPRINT_VELOCITY) + 0.01;
        if (Math.abs(angle) > maxAngle) {
          const overAngle = Math.abs(angle) - maxAngle;
          toDesired.addScaledVector(temporalMovement, -clamp((overAngle / Math.PI) * 3, 0, 1));
        }
      }
    }

    // (h) a per-frame budget on how much the velocity may change at all
    {
      let maxChange = 0.03;
      if (clip.animType === 'trip') maxChange *= 0.7;
      if (clip.animType === 'sliding') maxChange = 0.1;

      const veloFactor = Math.pow(
        normalizedClamp(vecLength(temporalMovement), 0, SPRINT_VELOCITY),
        1.5,
      );
      let firstStepFactor = veloFactor;
      if (clip.animType === 'movement') firstStepFactor *= 0.4;
      maxChange *=
        1 - firstStepFactor + firstStepFactor * curve(normalizedClamp(timeMs, 0, 160), 1.0);
      maxChange *= 1.2 - veloFactor * 0.4;
      maxChange *= 0.75 + config.agilityFactor * 0.5;
      maxChange *= powerFactor;

      normalizedMax(
        toDesired,
        Math.min(vecLength(toDesired), maxChange * TIME_STEP_MS),
        toDesired,
      );
    }

    // (i) air resistance — an acceleration ceiling that collapses as you near top speed.
    //     This, not a hard clamp, is what stops anyone exceeding their maximum.
    if (clip.animType !== 'sliding' && clip.animType !== 'deflect') {
      const falloffStartVelo = IDLE_DRIBBLE_SWITCH;
      scratch.copy(temporalMovement).add(toDesired);
      if (vecLength(scratch) > falloffStartVelo) {
        let accelPower = 11.0 * accelerationMultiplier;
        accelPower *= 1 - difficultyPenaltyFactor * 0.4;

        // The UNSCALED player maximum, deliberately, even for a touch clip.
        const veloAirResistanceFactor = clamp(
          Math.pow(
            clamp(
              (vecLength(temporalMovement) - falloffStartVelo) /
                (player.maxVelocity - falloffStartVelo),
              0,
              1,
            ),
            1.8,
          ),
          0,
          1,
        );

        const forwardVector = new Vector3(0, 0, 0);
        if (vecLength(scratch) > vecLength(temporalMovement)) {
          const accel = vecLength(scratch) - vecLength(temporalMovement);
          normalizedOrZero(scratch, forwardVector).multiplyScalar(accel);
        }

        const accelerationAddition = vecLength(forwardVector);
        const maxAccelerationMPS =
          accelPower * (1 - veloAirResistanceFactor) * (statAcceleration * 0.3 + 0.7);
        const maxAccelerationAddition = maxAccelerationMPS * (TIME_STEP_MS / 1000);
        if (accelerationAddition > maxAccelerationAddition) {
          const remainingFactor = maxAccelerationAddition / accelerationAddition;
          toDesired.addScaledVector(forwardVector, -(1 - remainingFactor));
        }
      }
    }

    // (j) integrate, with a hard snap to a velocity bucket over the clip's last two ticks.
    //     Without this a clip that is supposed to end at a standstill would trail off at
    //     walking pace and the next clip would start from the wrong bucket.
    scratch.copy(temporalMovement).add(toDesired);
    if (timeMs >= (clip.frameCount - 2) * 10) {
      const animOutgoingIsIdle = clip.outgoingVelocityEnum === Velocity.Idle;
      const resultIsIdle = floatToEnumVelocity(vecLength(scratch)) === Velocity.Idle;
      if (animOutgoingIsIdle && !resultIsIdle) {
        scratch.set(0, 0, 0);
      } else if (!animOutgoingIsIdle && resultIsIdle) {
        normalizedTo(rotate2D(clip.outgoingMovement, spatial.angle), DRIBBLE_VELOCITY, scratch);
      }
    }

    temporalMovement.copy(scratch);
    currentPosition.addScaledVector(temporalMovement, TIME_STEP_MS / 1000);
    positions.push(currentPosition.clone());
  }

  // ---- what the next clip inherits ---------------------------------------
  const resultingMovement = temporalMovement.clone();
  let rotationOffset: number;
  if (
    clip.outgoingVelocityEnum !== Velocity.Idle &&
    floatToEnumVelocity(vecLength(resultingMovement)) !== Velocity.Idle
  ) {
    rotationOffset = angle2D(rotate2D(resultingMovement, -spatial.angle), clip.outgoingMovement);
  } else {
    rotationOffset = toDesiredAngleCapped * physicsBias;
  }

  return { resultingMovement, positions, rotationOffset };
}

/** humanoidbase.cpp:1617-1620 — the outgoing velocity of a generated track, in m/s. */
export function calculateOutgoingMovement(positions: Vector3[]): Vector3 {
  const n = positions.length;
  if (n < 2) return new Vector3(0, 0, 0);
  return positions[n - 1]!.clone().sub(positions[n - 2]!).multiplyScalar(100);
}
